import React, { FC, useState } from 'react'

import {
  Typography,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Checkbox,
  Button,
  FormControlLabel,
} from '@mui/material'
import { useMedicationUpdate } from '@/apps/legacy/shared/contexts/MedicationUpdateContext'
import { useToast } from '@/shared/contexts/ToastContext'
import { createMedicationRegex } from '@/shared/constants/medicationPatterns'

// legacy dialog 色. 旧 colorTokens 値を hardcode 再現することで shared constants 依存を解消.
const LEGACY_DIALOG_COLORS = {
  checkbox: { unchecked: '#BDBDBD', checked: '#757575' },
  listItem: {
    selectedBg: '#F5F5F5',
    hoverBg: '#EEEEEE',
    selectedText: '#333333',
    normalText: '#666666',
  },
  button: {
    cancel: { text: '#666666', hoverBg: '#F5F5F5' },
    confirm: {
      bg: '#757575',
      text: '#FFFFFF',
      hoverBg: '#616161',
      disabledBg: '#E0E0E0',
      disabledText: '#9E9E9E',
    },
  },
  placeholder: { bg: '#D9E5FF', hoverBg: '#b8c9fa' },
} as const

type Props = {
  text: string
  medInfo: string[]
  onReplace: (
    original: string,
    replacement: string,
    replaceAll?: boolean
  ) => Promise<boolean>
}

export const MedicationPlaceholderText: FC<Props> = ({
  text,
  medInfo,
  onReplace,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [currentPlaceholder, setCurrentPlaceholder] = useState<string>('')
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [selectedMedications, setSelectedMedications] = useState<string[]>([])
  const [replaceAll, setReplaceAll] = useState<boolean>(false)

  // 薬品プレースホルダーと薬品リストを認識する正規表現
  const medPlaceholderRegex = createMedicationRegex(medInfo)

  // Split text by medication placeholders
  const renderText = () => {
    const parts: { text: string; isPlaceholder: boolean }[] = []
    let lastIndex = 0
    let match

    // Reset the regex state
    medPlaceholderRegex.lastIndex = 0

    while ((match = medPlaceholderRegex.exec(text)) !== null) {
      // Add the text before the placeholder
      if (match.index > lastIndex) {
        parts.push({
          text: text.substring(lastIndex, match.index),
          isPlaceholder: false,
        })
      }

      // Add the placeholder
      parts.push({
        text: match[0],
        isPlaceholder: true,
      })

      lastIndex = match.index + match[0].length
    }

    // Add any remaining text
    if (lastIndex < text.length) {
      parts.push({
        text: text.substring(lastIndex),
        isPlaceholder: false,
      })
    }

    return parts.map((part, index) =>
      part.isPlaceholder ? (
        <Tooltip key={index} title="クリックして薬剤を選択" arrow>
          <Box
            component="span"
            sx={{
              backgroundColor: LEGACY_DIALOG_COLORS.placeholder.bg,
              padding: '0 2px',
              cursor: 'pointer',
              borderRadius: '2px',
              '&:hover': {
                backgroundColor: LEGACY_DIALOG_COLORS.placeholder.hoverBg,
              },
            }}
            onClick={() => {
              setCurrentPlaceholder(part.text)
              // 既存の薬品リストの場合は、選択済みとして初期化
              if (part.text.includes('、')) {
                const meds = part.text
                  .split('、')
                  .map((m) => m.trim())
                  .filter((m) => medInfo.includes(m))
                setSelectedMedications(meds)
              } else if (medInfo.includes(part.text)) {
                setSelectedMedications([part.text])
              } else {
                setSelectedMedications([])
              }
              setIsDialogOpen(true)
            }}
          >
            {part.text}
          </Box>
        </Tooltip>
      ) : (
        <span key={index}>{part.text}</span>
      )
    )
  }

  // 薬剤更新コンテキストを使用
  const { isUpdating: isMedicationUpdating, setUpdating: setGlobalUpdating } =
    useMedicationUpdate()

  // トーストコンテキストを使用
  const { showToast } = useToast()

  const handleToggleMedication = (medication: string) => {
    setSelectedMedications((prev) => {
      if (prev.includes(medication)) {
        return prev.filter((m) => m !== medication)
      } else {
        return [...prev, medication]
      }
    })
  }

  const handleConfirmSelection = async () => {
    // 更新中の場合は操作をブロック
    if (isMedicationUpdating || selectedMedications.length === 0) {
      return
    }

    try {
      setIsUpdating(true)

      // 複数の薬品を読点区切りで結合
      const replacementText = selectedMedications.join('、')

      // 非同期処理のためにawaitを使用
      const result = await onReplace(
        currentPlaceholder,
        replacementText,
        replaceAll
      )

      if (!result) {
        // エラーが発生した場合はユーザーに通知
        showToast('薬品の置換に失敗しました', 'error', 3000)
      }
    } catch (_error) {
      // エラーハンドリング：ユーザーに通知
      showToast('薬品置換中にエラーが発生しました', 'error', 3000)
    } finally {
      // 処理完了後に必ずローディング状態をリセット
      setIsUpdating(false)
      setIsDialogOpen(false)
      setSelectedMedications([])
      setReplaceAll(false)

      // 念のため、グローバル更新フラグも強制的にfalseに設定
      // onReplaceの処理中に何らかの理由でフラグがリセットされなかった場合の保険
      setGlobalUpdating(false)
    }
  }

  const handleCloseDialog = () => {
    if (!isUpdating) {
      setIsDialogOpen(false)
      setSelectedMedications([])
      setReplaceAll(false)
    }
  }

  return (
    <>
      <Box component="span" sx={{ display: 'inline' }}>
        {renderText()}
      </Box>
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6">薬剤を選択</Typography>
            <Typography
              variant="subtitle2"
              sx={{ color: 'text.secondary', mt: 1 }}
            >
              {currentPlaceholder}{' '}
              を置き換える薬剤を選択してください（複数選択可）
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {isUpdating ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={40} />
              <Typography sx={{ ml: 2, mt: 0.5 }}>
                薬剤情報を更新中...
              </Typography>
            </Box>
          ) : (
            <List sx={{ pt: 0 }}>
              {medInfo.map((med, index) => {
                const isSelected = selectedMedications.includes(med)
                return (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      onClick={() => handleToggleMedication(med)}
                      sx={[
                        {
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor:
                              LEGACY_DIALOG_COLORS.listItem.hoverBg,
                          },
                        },
                        isSelected
                          ? {
                              backgroundColor:
                                LEGACY_DIALOG_COLORS.listItem.selectedBg,
                            }
                          : {
                              backgroundColor: 'transparent',
                            },
                      ]}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          sx={{
                            color: LEGACY_DIALOG_COLORS.checkbox.unchecked,
                            '&.Mui-checked': {
                              color: LEGACY_DIALOG_COLORS.checkbox.checked,
                            },
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={med}
                        primaryTypographyProps={{
                          color: isSelected
                            ? LEGACY_DIALOG_COLORS.listItem.selectedText
                            : LEGACY_DIALOG_COLORS.listItem.normalText,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={replaceAll}
                onChange={(e) => setReplaceAll(e.target.checked)}
                disabled={isUpdating}
                sx={{
                  color: LEGACY_DIALOG_COLORS.checkbox.unchecked,
                  '&.Mui-checked': {
                    color: LEGACY_DIALOG_COLORS.checkbox.checked,
                  },
                }}
              />
            }
            label="同じ文字をまとめて置換"
            sx={{
              color: 'text.secondary',
            }}
          />
          <Box>
            <Button
              onClick={handleCloseDialog}
              disabled={isUpdating}
              sx={{
                color: LEGACY_DIALOG_COLORS.button.cancel.text,
                '&:hover': {
                  backgroundColor: LEGACY_DIALOG_COLORS.button.cancel.hoverBg,
                },
              }}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleConfirmSelection}
              variant="contained"
              disabled={isUpdating || selectedMedications.length === 0}
              sx={{
                backgroundColor: LEGACY_DIALOG_COLORS.button.confirm.bg,
                color: LEGACY_DIALOG_COLORS.button.confirm.text,
                '&:hover': {
                  backgroundColor: LEGACY_DIALOG_COLORS.button.confirm.hoverBg,
                },
                '&:disabled': {
                  backgroundColor:
                    LEGACY_DIALOG_COLORS.button.confirm.disabledBg,
                  color: LEGACY_DIALOG_COLORS.button.confirm.disabledText,
                },
              }}
            >
              確定
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}
